import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  classifyDrag, edgeRejected, releaseDecision, rubberBand, settleDuration, VelocityTracker,
} from './gesture';
import { arrowDirection, createKeyRepeater, shouldIgnoreKey, turnDuration } from './keys';
import { getTransition } from './transitions';

// ── Turning between pages like a book ───────────────────────────────────────
// One chapter (The Bible) or one day (Reading) is a page. Within it the reader
// scrolls up and down as always; the turn is between pages:
//   * a sideways drag with one finger pulls the page along, with the
//     neighbouring page sliding in beside it; let go past ~35% of the width,
//     or flick, and it turns, otherwise it springs back;
//   * ← / → turn one page, and holding the key keeps turning, faster;
//   * turn(dir) from buttons, and turn(dir, { target }) for a jump (the
//     chapter scrubber) that still lands with one turn.
//
// The neighbouring page is drawn by the caller (renderNeighbour) from data it
// already has or has prefetched, so what slides in is real text. When the
// turn completes, that drawing stays on screen as a cover over the page until
// the real page reports `ready` for its new pageKey; then the cover goes in
// the same frame as the page snaps back into place. That hides the moment the
// old text is swapped for the new, and lets the window be scrolled to the top
// of the new page underneath it.
//
// Options (read fresh on every call, so callers can pass plain values):
//   canPrev, canNext       whether there is a page each way
//   onTurn(dir, target, { step })  go there (update the URL). step > 0 when
//                          the turn comes from a held key (callers replace
//                          history then rather than piling up entries)
//   renderNeighbour(dir, target)   the neighbouring page's drawing
//   pageKey, ready         which page is showing, and whether its content is
//                          loaded (the cover waits for both)
//   getTopInset()          px hidden under sticky bars at the top
//   transition             a name from transitions.js (default 'slide')
//   keyboard               listen for ← / →
//   exclude                extra CSS selector where drags must not start
//
// Returns { frameProps, pageRef, layer, turn }. Spread frameProps on the
// element that clips the page (TurnPage does this), attach pageRef to the
// page that moves, and render `layer` anywhere that stays mounted.

// Where a drag must never start: controls that drag sideways themselves
// (audio scrubbing, sliders, scrolling strips), text fields, and the
// annotation toolbar. Buttons are fine; a swipe across a verse number is
// still a swipe.
const NO_DRAG = 'audio, video, input, textarea, select, [contenteditable=""], [contenteditable="true"], '
  + '[role="slider"], .no-page-turn, .anno-toolbar, .bible-chapter-grid, .bible-verse-jump-strip, '
  + '.bible-verse-controls, iframe';

// A finger held this long before it moves is a long-press: the browser's text
// selection, not a turn.
const LONG_PRESS_MS = 350;
// Nor does a drag start this soon after a text selection changed.
const AFTER_SELECTION_MS = 400;
// The cover never waits longer than this for the new page to load.
const COVER_TIMEOUT_MS = 2500;
// A click this soon after a drag ended is the drag's tail, not a tap.
const CLICK_AFTER_DRAG_MS = 400;

const reducedMotion = () => !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
const now = () => (window.performance?.now ? performance.now() : Date.now());

function hasSelection() {
  const sel = window.getSelection?.();
  return !!sel && !sel.isCollapsed && !!String(sel).trim();
}

export function usePageTurn(options) {
  const optsRef = useRef(options);
  optsRef.current = options;

  const frameRef = useRef(null);
  const pageRef = useRef(null);
  const incomingRef = useRef(null);
  const els = () => ({ page: pageRef.current, incoming: incomingRef.current });
  // The neighbouring page on screen: { dir, target, geo, el }. `el` is
  // null while it follows live props (dragging), and a frozen drawing once
  // the turn has committed (the props then describe the new page).
  const [layer, setLayer] = useState(null);
  const layerRef = useRef(null);
  layerRef.current = layer;

  // 'idle' | 'dragging' | 'settling' | 'covering'
  const phase = useRef('idle');
  const drag = useRef(null);
  const anim = useRef(null);
  const cover = useRef(null);
  const pendingAnim = useRef(null);
  const lastSelectionAt = useRef(-Infinity);
  const lastDragEndAt = useRef(-Infinity);

  const transition = () => getTransition(optsRef.current.transition);
  const can = dir => (dir > 0 ? !!optsRef.current.canNext : dir < 0 ? !!optsRef.current.canPrev : false);

  // The page window on screen: the frame's columns, from below the sticky
  // bars (or the frame's own top, if that is lower) to the bottom of the view.
  const measure = () => {
    const frame = frameRef.current;
    if (!frame) return null;
    const r = frame.getBoundingClientRect();
    const inset = Math.max(0, optsRef.current.getTopInset?.() || 0);
    const top = Math.max(r.top, inset);
    return { left: r.left, top, width: r.width, height: Math.max(0, window.innerHeight - top) };
  };

  // The page only needs its own layer while it moves.
  const setWillChange = on => {
    const moves = on && transition().movesPage;
    if (pageRef.current) pageRef.current.style.willChange = moves ? 'transform' : '';
    frameRef.current?.classList.toggle('pt-moving', on);
  };

  // Line the top of the page up with the top of the cover, so the new page
  // starts where its drawing did. Only ever scrolls up (a reader near the top
  // of a short chapter stays put).
  const alignScroll = geoTop => {
    const frame = frameRef.current;
    if (!frame) return;
    const r = frame.getBoundingClientRect();
    if (r.top < geoTop - 1) window.scrollTo(0, Math.max(0, window.scrollY + r.top - geoTop));
  };

  const reveal = useCallback(() => {
    const c = cover.current;
    if (c) clearTimeout(c.timer);
    cover.current = null;
    if (pageRef.current) pageRef.current.style.transform = '';
    setWillChange(false);
    phase.current = 'idle';
    setLayer(null);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- refs only

  const cleanupCancelled = () => {
    if (pageRef.current) pageRef.current.style.transform = '';
    setWillChange(false);
    phase.current = 'idle';
    setLayer(null);
  };

  // Whatever is in flight jumps to its end, so a new turn starts clean.
  const fastForward = () => {
    if (anim.current) { const a = anim.current; anim.current = null; a.finish(); }
    if (phase.current === 'covering') reveal();
    if (phase.current === 'dragging') { drag.current = null; cleanupCancelled(); }
  };

  // The same, for a turn about to start: an animation in flight lands first.
  const landInFlight = () => {
    if (anim.current) { const a = anim.current; anim.current = null; a.finish(); }
    fastForward();
  };

  const commit = (dir, target, step, geo) => {
    const o = optsRef.current;
    phase.current = 'covering';
    // Freeze the drawing of the page we are turning to before the props move
    // on to describe it as the current page.
    const el = o.renderNeighbour?.(dir, target) ?? false;
    setLayer(l => (l ? { ...l, el } : l));
    if (geo) alignScroll(geo.top);
    cover.current = { key: o.pageKey, timer: setTimeout(reveal, COVER_TIMEOUT_MS) };
    o.onTurn?.(dir, target, { step });
  };

  // No animation: reduced motion, or nothing on screen to animate.
  const commitInstant = (dir, target, step) => {
    const o = optsRef.current;
    const geo = measure();
    if (geo) alignScroll(geo.top);
    o.onTurn?.(dir, target, { step });
  };

  // Turn from code: a button, a key, the scrubber. Returns whether it turned.
  const turn = useCallback((dir, { target, step = 0 } = {}) => {
    if (!dir || (!target && !can(dir))) return false;
    landInFlight();
    const geo = measure();
    if (reducedMotion() || !geo || !geo.width) { commitInstant(dir, target, step); return true; }
    phase.current = 'settling';
    const t = transition();
    const duration = t.turnDuration ? t.turnDuration(step) : turnDuration(step);
    pendingAnim.current = { dir, target, step, geo, from: 0, duration, commit: true };
    setLayer({ dir, target, geo, el: null });
    return true;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const turnRef = useRef(turn);
  turnRef.current = turn;

  const runAnim = ({ dir, target, step, geo, from, to, duration, commit: doCommit }) => {
    const t = transition();
    setWillChange(true);
    anim.current = t.animate({
      ...els(), dir, from, to, width: geo.width, duration,
      onDone: () => {
        anim.current = null;
        if (doCommit) commit(dir, target, step, geo);
        else cleanupCancelled();
      },
    });
  };

  // Once the neighbouring page is in the DOM: start a pending turn, or put it
  // where the finger already is, before the browser paints it.
  useLayoutEffect(() => {
    if (!layer) return;
    const p = pendingAnim.current;
    if (p) {
      pendingAnim.current = null;
      runAnim({ ...p, to: -p.dir * p.geo.width });
      return;
    }
    const d = drag.current;
    if (d && d.state === 'dragging' && layer.el == null) {
      transition().frame({ ...els(), dir: d.dir, offset: d.offset, width: d.geo.width });
    }
  }, [layer]); // eslint-disable-line react-hooks/exhaustive-deps

  // The new page is in: lift the cover (after it has had a frame to paint).
  useEffect(() => {
    const c = cover.current;
    if (!c || options.pageKey === c.key || !options.ready) return;
    const id = requestAnimationFrame(() => { if (cover.current === c) reveal(); });
    return () => cancelAnimationFrame(id);
  }, [options.pageKey, options.ready, reveal]);

  // Leaving the page mid-turn: stop timers and animations.
  useEffect(() => () => {
    if (cover.current) clearTimeout(cover.current.timer);
    if (anim.current) { const a = anim.current; anim.current = null; a.cancel?.(); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- refs only

  // Selections, to keep a long-press selection from becoming a drag.
  useEffect(() => {
    const onSel = () => { if (hasSelection()) lastSelectionAt.current = now(); };
    document.addEventListener('selectionchange', onSel);
    return () => document.removeEventListener('selectionchange', onSel);
  }, []);

  // ── Keyboard ────────────────────────────────────────────────────────────
  const keyboard = options.keyboard !== false;
  useEffect(() => {
    if (!keyboard) return;
    const repeater = createKeyRepeater({ onStep: (dir, step) => turnRef.current(dir, { step }) });
    const down = e => {
      const dir = arrowDirection(e.key);
      if (!dir) return;
      if (shouldIgnoreKey(e, document)) return;
      e.preventDefault();
      if (e.repeat) return;          // our own timer does the repeating
      repeater.press(dir);
    };
    const up = e => { const dir = arrowDirection(e.key); if (dir) repeater.release(dir); };
    const stop = () => repeater.stop();
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', stop);
    document.addEventListener('visibilitychange', stop);
    return () => {
      repeater.stop();
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', stop);
      document.removeEventListener('visibilitychange', stop);
    };
  }, [keyboard]);

  // ── Touch ───────────────────────────────────────────────────────────────
  // Pointer events with touch-action: pan-y on the frame: the browser keeps
  // vertical scrolling (and cancels our pointer when it takes one over),
  // while sideways movement comes to us. Mouse and pen are left alone, since
  // dragging with them selects text.
  const onPointerDown = e => {
    if (e.pointerType !== 'touch' || !e.isPrimary) return;
    const o = optsRef.current;
    if (o.disabled) return;
    if (edgeRejected(e.clientX, window.innerWidth)) return;
    const exclude = o.exclude ? `${NO_DRAG}, ${o.exclude}` : NO_DRAG;
    if (e.target?.closest?.(exclude)) return;
    if (hasSelection() || now() - lastSelectionAt.current < AFTER_SELECTION_MS) return;
    if (phase.current !== 'idle') landInFlight();
    drag.current = {
      id: e.pointerId, x0: e.clientX, y0: e.clientY, t0: now(), state: 'pending',
      dir: 0, offset: 0, dx: 0, geo: null, tracker: new VelocityTracker(),
    };
  };

  const onPointerMove = e => {
    const d = drag.current;
    if (!d || e.pointerId !== d.id) return;
    const dx = e.clientX - d.x0, dy = e.clientY - d.y0;
    if (d.state === 'pending') {
      const kind = classifyDrag(dx, dy);
      if (kind === 'pending') {
        if (now() - d.t0 > LONG_PRESS_MS) drag.current = null;   // a long-press
        return;
      }
      if (kind === 'vertical' || now() - d.t0 > LONG_PRESS_MS || hasSelection()) {
        drag.current = null;
        return;
      }
      const geo = measure();
      if (!geo) { drag.current = null; return; }
      d.state = 'dragging';
      d.geo = geo;
      // Start from where the drag was recognised, so the page does not jump
      // by the slop distance.
      d.x0 = e.clientX - Math.sign(dx) * 2;
      phase.current = 'dragging';
      try { frameRef.current?.setPointerCapture?.(e.pointerId); } catch (_) { /* already released */ }
      if (!reducedMotion()) setWillChange(true);
    }
    // Dragging.
    const ddx = e.clientX - d.x0;
    d.dx = ddx;
    d.tracker.add(e.clientX, now());
    if (reducedMotion()) return;     // no following; the release decides
    const dir = ddx < 0 ? 1 : ddx > 0 ? -1 : d.dir;
    const offset = can(dir) ? ddx : rubberBand(ddx, d.geo.width);
    d.offset = offset;
    const wantDir = can(dir) ? dir : 0;
    if (wantDir !== d.dir) {
      d.dir = wantDir;
      setLayer(wantDir ? { dir: wantDir, target: undefined, geo: d.geo, el: null } : null);
      if (!wantDir) incomingRef.current = null;
    }
    transition().frame({ ...els(), dir: d.dir, offset, width: d.geo.width });
  };

  const endDrag = (e, cancelled) => {
    const d = drag.current;
    if (!d || e.pointerId !== d.id) return;
    drag.current = null;
    if (d.state !== 'dragging') return;
    lastDragEndAt.current = now();
    const o = optsRef.current;
    const vx = d.tracker.velocity();
    const width = d.geo.width;
    if (reducedMotion()) {
      phase.current = 'idle';
      setWillChange(false);
      const decision = cancelled ? 'cancel'
        : releaseDecision({ dx: d.dx, vx, width: Math.min(width, 220), canPrev: o.canPrev, canNext: o.canNext });
      if (decision !== 'cancel') commitInstant(decision === 'next' ? 1 : -1, undefined, 0);
      return;
    }
    const decision = cancelled ? 'cancel'
      : releaseDecision({ dx: d.offset, vx, width, canPrev: o.canPrev, canNext: o.canNext });
    phase.current = 'settling';
    if (decision === 'cancel') {
      runAnim({ dir: d.dir, geo: d.geo, from: d.offset, to: 0, duration: settleDuration(d.offset, width, vx, 260), commit: false });
      return;
    }
    const dir = decision === 'next' ? 1 : -1;
    const to = -dir * width;
    runAnim({ dir, target: undefined, step: 0, geo: d.geo, from: d.offset, to, duration: settleDuration(to - d.offset, width, vx), commit: true });
  };

  const onPointerUp = e => endDrag(e, false);
  const onPointerCancel = e => endDrag(e, true);
  // Android shows its long-press menu with a contextmenu event.
  const onContextMenu = () => {
    const d = drag.current;
    if (d && d.state === 'pending') drag.current = null;
  };
  const onClickCapture = e => {
    if (now() - lastDragEndAt.current < CLICK_AFTER_DRAG_MS) { e.preventDefault(); e.stopPropagation(); }
  };

  const frameProps = {
    ref: frameRef,
    onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onContextMenu, onClickCapture,
  };

  const renderNeighbour = options.renderNeighbour;
  const transitionName = getTransition(options.transition).name;
  const layerNode = useMemo(() => {
    if (!layer) return null;
    const { geo, dir } = layer;
    const drawing = layer.el ?? renderNeighbour?.(dir, layer.target);
    const t = getTransition(transitionName);
    // Where the incoming page starts, so it never shows in the wrong place
    // before the first frame is drawn. The same value on every render, so
    // React never undoes what frame() and animate() have set since.
    const init = t.initial(dir, geo.width);
    return (
      // inert: the drawing (an outline page's tree, say) holds buttons that
      // must not take focus while it is on screen.
      <div className="pt-cover" aria-hidden="true" inert
        style={{ left: geo.left, top: geo.top, width: geo.width, height: geo.height }}>
        <div ref={incomingRef} className={`pt-incoming pt-incoming-${dir > 0 ? 'next' : 'prev'}`}
          style={init.incoming}>
          {drawing}
        </div>
      </div>
    );
  }, [layer, renderNeighbour, transitionName]);

  return { frameProps, pageRef, layer: layerNode, turn };
}

// The frame and the moving page, for the common case.
export function TurnPage({ turner, className = '', children }) {
  return (
    <div {...turner.frameProps} className={`pt-frame ${className}`.trim()}>
      <div ref={turner.pageRef} className="pt-page">{children}</div>
    </div>
  );
}
